import { DenormalizedFormat } from '@aipb/gulliver/dropbox/model';
import { OpenLayersModel } from '@aipb/shared/ui/open-layers';
import { createAspectBox } from '@aipb/shared/util/open-layers';
import { SnackbarService } from '@aipb/shared/util/snackbar';
import { inject, Injectable, NgZone } from '@angular/core';
import { SafeUrl } from '@angular/platform-browser';
import { TranslocoService } from '@jsverse/transloco';
import { Feature, Map, View } from 'ol';
import { Control, defaults as controlDefaults } from 'ol/control';
import { containsCoordinate, containsExtent, getCenter } from 'ol/extent';
import { SimpleGeometry } from 'ol/geom';
import { defaults, Draw, Interaction } from 'ol/interaction';
import { createBox } from 'ol/interaction/Draw';
import BaseLayer from 'ol/layer/Base';
import ImageLayer from 'ol/layer/Image';
import VectorLayer from 'ol/layer/Vector';
import MapBrowserEvent from 'ol/MapBrowserEvent';
import { Projection } from 'ol/proj';
import Static from 'ol/source/ImageStatic';
import VectorSource from 'ol/source/Vector';
import { Observable, Subject } from 'rxjs';
import { Tool } from '../model/tool.model';

export const TOOL_FREE = 'free';

@Injectable()
export class ImagePreviewOpenLayersService extends OpenLayersModel {
  private _drawSource: VectorSource<SimpleGeometry>;
  private _imageSource: Static;

  private _imageUrl: string | SafeUrl;
  private _extent;
  private _projection;
  private _tools: { [key in Tool]?: Draw } = {};
  private _formats: DenormalizedFormat[];
  private _drawEnd: Subject<void> = new Subject<void>();
  public drawEnd$: Observable<void> = this._drawEnd.asObservable();
  private snackbar = inject(SnackbarService);
  private transloco = inject(TranslocoService);
  private zone = inject(NgZone);

  private _drawnFeature;

  constructor() {
    super();
  }

  private _imageWidth: number;

  get imageWidth() {
    return this._imageWidth;
  }

  private _imageHeight: number;

  get imageHeight() {
    return this._imageHeight;
  }

  get cropFormats(): DenormalizedFormat[] {
    return this._formats;
  }

  public init(imageUrl: string | SafeUrl, formats: DenormalizedFormat[]): void {
    this._imageUrl = imageUrl;
    this._formats = formats;

    const img = new Image();
    img.onload = () => {
      this._imageWidth = img.width;
      this._imageHeight = img.height;
      this._extent = [0, 0, this._imageWidth, this._imageHeight];
      this._projection = new Projection({
        code: 'xkcd-image',
        units: 'pixels',
        extent: this._extent
      });
      this.initialize();
    };
    img.src = this._imageUrl.toString();
  }

  public updateMap(map: Map): void {
    this.setMap(map);

    if (!this._imageSource) return;

    map.getView().fit(this._imageSource.getImageExtent(), { size: map.getSize() });
  }

  public initTool(tool: Tool): void {
    this.disableTools();
    this.clearDraws();

    if (!(tool in this._tools)) return;

    this._tools[tool].setActive(true);
  }

  public getActiveDraw(): Feature<SimpleGeometry> {
    if (this._drawSource.getFeatures().length === 0) return null;

    return this._drawSource.getFeatures()[0];
  }

  protected initLayers(): BaseLayer[] {
    this._imageSource = new Static({
      url: this._imageUrl.toString(),
      projection: this._projection,
      imageExtent: this._extent
    });
    const imageLayer = new ImageLayer({
      source: this._imageSource
    });

    this._drawSource = new VectorSource<SimpleGeometry>();

    const drawLayer = new VectorLayer({
      source: this._drawSource
    });

    return [imageLayer, drawLayer];
  }

  protected initInteractions(): Interaction[] {
    const conditionInsideImage = (mapBrowserEvent: MapBrowserEvent<any>) => {
      const extent = this._imageSource.getImageExtent();
      let isInside = true;

      if (this._drawnFeature) {
        isInside = containsExtent(extent, this._drawnFeature.getGeometry().getExtent());
      } else {
        isInside = containsCoordinate(extent, mapBrowserEvent.coordinate);
      }

      if (!isInside) {
        this.zone.run(() => {
          this.snackbar.open('image_preview.messages.crop_has_to_be_inside_image', 'warn');
        });
      }

      return isInside;
    };

    this._tools = {
      box: new Draw({
        source: this._drawSource,
        type: 'Circle',
        geometryFunction: createBox(),
        condition: conditionInsideImage
      })
    };

    for (const cropFormat of this.cropFormats) {
      this._tools[cropFormat.cropRatio[0]] = new Draw({
        source: this._drawSource,
        type: 'Circle',
        condition: conditionInsideImage,
        geometryFunction:
          cropFormat.cropRatio[0] === TOOL_FREE ? createBox() : createAspectBox(cropFormat.width / cropFormat.height)
      });
    }

    this.disableTools();

    const interactions: Interaction[] = [];

    for (const key of Object.keys(this._tools)) {
      this._tools[key].on('drawstart', (evt) => {
        this._drawnFeature = evt.feature;
      });
      this._tools[key].on('drawend', () => {
        this.clearDraws();
        this._drawnFeature = null;
        this._drawEnd.next();
      });
      interactions.push(this._tools[key]);
    }

    return [...defaults().getArray(), ...interactions];
  }

  protected initView(): View {
    return new View({
      projection: this._projection,
      center: getCenter(this._extent),
      zoom: 1
    });
  }

  protected initControls(): Control[] {
    return controlDefaults().getArray();
  }

  private disableTools(): void {
    if (!this._tools) return;

    for (const key of Object.keys(this._tools)) {
      this._tools[key].setActive(false);
    }
  }

  private clearDraws(): void {
    if (!this._drawSource) return;

    this._drawSource.clear();
  }
}
